import { API_CALL } from 'actions/actionConstants'
import { BUSINESS_TRAVELLER_API_ACTIONS } from 'reducers/businessTravellerApiActionReducers'
import { GoogleLoginData } from 'actions/AuthActions'
import { AppAction } from 'actions/types/ActionTypes'
import { requestPostGoogleLinkPersonalAccount } from 'businessTraveller/api/requestPostGoogleLinkPersonalAccount'
import requestPatchMyCreditCustomerId from 'businessTraveller/api/requestPatchMyCreditCustomerId'
import * as authService from 'api/auth'
import { LE_BUSINESS_TRAVELLER, LUXURY_ESCAPES } from 'constants/brands'
import { validateEmployeeAccounts } from 'businessTraveller/utils/validateOnboardingAccounts'

export const BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_CALLBACK_SOURCE = 'business-traveller-business-signup-link-account'
export const BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_SUCCESS_EVENT = 'business-traveller-business-signup-google-link-account-success'
export const BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_FAILURE_EVENT = 'business-traveller-business-signup-google-link-account-failure'

export function loginGoogle(data: GoogleLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const transformedData = {
      ...data,
      country_code: state.geo.currentRegionCode,
      signup_domain: window.location.hostname,
    }

    dispatch({
      type: API_CALL,
      api: BUSINESS_TRAVELLER_API_ACTIONS.USER_LINK_PERSONAL_ACCOUNT,
      request: () => requestPostGoogleLinkPersonalAccount(transformedData).then(data => {
        window.dispatchEvent(new CustomEvent('google-login-success', {
          detail: { email: transformedData.email }
        }))
        return data
      }),
      source: 'email',
    })
  }
}

// Flag-on personal-account linking via Google: authenticate the Google user against the LE brand
// (no session) to obtain their token, then link through the self-service endpoint which derives the
// account id from that verified token. Gated by lebt_dedicated_tenant_registration.
export function loginGoogleLinkPersonalAccountSelfService(data: GoogleLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const businessId = state.businessTraveller.currentBusinessAccount?.business?.id

    dispatch({
      type: API_CALL,
      api: BUSINESS_TRAVELLER_API_ACTIONS.USER_LINK_PERSONAL_ACCOUNT,
      request: async() => {
        if (!businessId) {
          throw new Error('Business not found')
        }
        const response = await authService.loginGoogle(
          data,
          state.geo.currentRegionCode,
          false,
          { brand: LUXURY_ESCAPES, includeCredentials: false },
        )
        const result = await requestPatchMyCreditCustomerId({ businessId, linkedAccountToken: response.accessToken })
        window.dispatchEvent(new CustomEvent('google-login-success', {
          detail: { email: data.email },
        }))
        return result
      },
      source: 'email',
    })
  }
}

// Flag-on links via the self-service /me endpoint; flag-off keeps the credential login-proxy.
export function getPersonalAccountLinkingGoogleAction(isDedicatedTenantRegistrationEnabled: boolean) {
  return isDedicatedTenantRegistrationEnabled ? loginGoogleLinkPersonalAccountSelfService : loginGoogle
}

export const BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_CALLBACK_SOURCE = 'business-traveller-employee-setup'
export const BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_SUCCESS_EVENT = 'business-traveller-employee-setup-google-success'
export const BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_FAILURE_EVENT = 'business-traveller-employee-setup-google-failure'

function loginGoogleForOnboarding(data: GoogleLoginData, successEvent: string, failureEvent: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    try {
      const personal = await authService.loginGoogle(data, state.geo.currentRegionCode, false, {
        brand: LUXURY_ESCAPES, includeCredentials: false,
      })
      const work = await authService.loginGoogle(data, state.geo.currentRegionCode, false, {
        brand: LE_BUSINESS_TRAVELLER,
      })
      validateEmployeeAccounts({ personal, work }, data.email)
      window.dispatchEvent(new CustomEvent(successEvent, {
        detail: { accessToken: personal.accessToken, account: personal.account, personal, work },
      }))
    } catch(error) {
      window.dispatchEvent(new CustomEvent(failureEvent, { detail: { error } }))
    }
  }
}

export function loginGoogleForBusinessSignup(data: GoogleLoginData): AppAction {
  return loginGoogleForOnboarding(data, BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_SUCCESS_EVENT,
    BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_FAILURE_EVENT)
}

export function loginGoogleForEmployeeSetup(data: GoogleLoginData): AppAction {
  return loginGoogleForOnboarding(data, BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_SUCCESS_EVENT,
    BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_FAILURE_EVENT)
}
